import { api } from '../api.js';
import { esc, money, dateTime, statusBadge, dialog, toast, guard } from '../ui.js';

const REASONS = ['Paid outside website (Venmo/Zelle/cash)', 'Comp guest', 'Partner or industry', 'VIP', 'Pay on arrival', 'Other'];

export async function bookingsView(root, me) {
  const manager = me.groups.some((g) => g === 'Owner' || g === 'Manager');
  const { slots } = await api('/experiences');
  const upcoming = slots.filter((s) => new Date(s.starts_at) > new Date(Date.now() - 86400000));
  const state = { slotId: upcoming[0]?.id || '', view: 'active' };

  root.innerHTML = `
    <div class="page-head">
      <h1>${manager ? 'Bookings' : 'My tours'}</h1>
      ${manager ? '<button class="btn small" id="new-booking" type="button">+ Phone booking</button>' : ''}
    </div>
    <div class="toolbar">
      <label class="field"><span>Date</span>
        <select id="slot">
          <option value="">All dates</option>
          ${slots.map((s) => `<option value="${s.id}" ${s.id === state.slotId ? 'selected' : ''}>${esc(dateTime(s.starts_at))} · ${esc(nameOf(s))} · ${s.capacity - s.seats_left}/${s.capacity} seats</option>`).join('')}
        </select>
      </label>
      ${manager ? `<div class="tabs" role="group" aria-label="Show">
        <button class="chip" type="button" data-view="active" aria-pressed="true">Bookings</button>
        <button class="chip" type="button" data-view="abandoned" aria-pressed="false">Abandoned checkouts</button>
      </div>` : ''}
    </div>
    <div id="summary" class="stats"></div>
    <div id="table"></div>`;

  function nameOf(s) { return s.experience_name || ''; }

  const load = guard(async () => {
    const qs = new URLSearchParams();
    if (state.slotId) qs.set('slotId', state.slotId);
    if (state.view === 'abandoned') qs.set('view', 'abandoned');
    const { bookings } = await api(`/bookings?${qs}`);
    const slot = slots.find((s) => s.id === state.slotId);
    const confirmed = bookings.filter((b) => ['deposit_paid', 'paid_in_full'].includes(b.status));
    const guests = confirmed.reduce((n, b) => n + b.guest_count, 0);
    root.querySelector('#summary').innerHTML = state.view === 'abandoned' ? '' : `
      <div class="stat"><span class="v">${guests}${slot ? ` / ${slot.capacity}` : ''}</span><span class="k">Guests confirmed</span></div>
      <div class="stat"><span class="v">${confirmed.length}</span><span class="k">Bookings</span></div>
      ${manager ? `<div class="stat"><span class="v">${money(confirmed.reduce((n, b) => n + (b.paid_cents || 0), 0))}</span><span class="k">Collected</span></div>
      <div class="stat"><span class="v">${money(confirmed.reduce((n, b) => n + Math.max(0, b.total_cents - (b.paid_cents || 0)), 0))}</span><span class="k">Balance outstanding</span></div>` : ''}`;

    if (!bookings.length) {
      root.querySelector('#table').innerHTML = `<p class="notice">${state.view === 'abandoned' ? 'No abandoned checkouts in the last 14 days.' : 'No bookings for this date yet.'}</p>`;
      return;
    }
    root.querySelector('#table').innerHTML = `<div class="table-wrap"><table class="data">
      <thead><tr><th>Guest</th><th>Party</th>${manager ? '<th>Paid</th>' : ''}<th>Date</th><th>Status</th><th></th></tr></thead>
      <tbody>${bookings.map((b) => `<tr>
        <td><strong>${esc([b.first_name, b.last_name].filter(Boolean).join(' ') || '(no name)')}</strong>
            <div class="small muted">${esc(b.email || '')}${b.phone ? ` · ${esc(b.phone)}` : ''}</div>
            ${b.allergies ? `<div class="small"><strong>Allergies:</strong> ${esc(b.allergies)}</div>` : ''}
            ${b.favorite_song ? `<div class="small">Favorite song: ${esc(b.favorite_song)}</div>` : ''}
            ${b.bay_area_wish ? `<div class="small">Always wanted to: ${esc(b.bay_area_wish)}</div>` : ''}</td>
        <td>${b.guest_count}</td>
        ${manager ? `<td>${money(b.paid_cents)} <span class="small muted">of ${money(b.total_cents)}</span>${b.deposit_override_reason ? `<div class="small muted">Override: ${esc(b.deposit_override_reason)}</div>` : ''}</td>` : ''}
        <td class="small">${esc(dateTime(b.starts_at))}<div class="muted">${esc(b.experience)}</div></td>
        <td>${statusBadge(b.status === 'held' && state.view === 'abandoned' ? 'expired' : b.status)}</td>
        <td style="text-align:right;white-space:nowrap">${manager ? actions(b) : ''}</td>
      </tr>`).join('')}</tbody></table></div>`;
  });

  function actions(b) {
    const out = [];
    if (['held', 'expired'].includes(b.status)) out.push(`<button class="btn small" type="button" data-override="${b.id}" data-name="${esc(b.first_name || '')}" data-deposit="${b.deposit_cents}">Confirm without deposit</button>`);
    if (['held', 'deposit_paid', 'paid_in_full'].includes(b.status)) out.push(`<button class="btn small danger" type="button" data-cancel="${b.id}">Cancel</button>`);
    return out.join(' ');
  }

  root.querySelector('#slot').addEventListener('change', (e) => { state.slotId = e.target.value; load(); });
  root.querySelectorAll('[data-view]').forEach((b) => b.addEventListener('click', () => {
    state.view = b.dataset.view;
    root.querySelectorAll('[data-view]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    load();
  }));

  root.querySelector('#table').addEventListener('click', guard(async (e) => {
    const ov = e.target.closest('[data-override]');
    const cancel = e.target.closest('[data-cancel]');
    if (ov) {
      const f = await dialog(`<h2>Confirm without deposit</h2>
        <p class="small muted">${esc(ov.dataset.name || 'This guest')} · ${money(Number(ov.dataset.deposit))} deposit will be waived. This is recorded with your name.</p>
        ${reasonFields()}
        <div class="dialog-actions"><button class="btn small" value="cancel" formnovalidate>Back</button><button class="btn small primary" value="ok">Confirm booking</button></div>`, needNote);
      if (!f) return;
      await api(`/bookings/${ov.dataset.override}/override`, { method: 'POST', body: { reason: f.get('reason'), note: f.get('note') } });
      toast('Booking confirmed');
      load();
    }
    if (cancel) {
      const f = await dialog(`<h2>Cancel this booking?</h2>
        <p class="small muted">Their seats are released right away. Refunds are issued from the Stripe dashboard and show up here automatically.</p>
        <label class="field"><span>Note (optional)</span><textarea name="note" rows="2"></textarea></label>
        <div class="dialog-actions"><button class="btn small" value="cancel" formnovalidate>Keep booking</button><button class="btn small danger" value="ok">Cancel booking</button></div>`);
      if (!f) return;
      const r = await api(`/bookings/${cancel.dataset.cancel}/cancel`, { method: 'POST', body: { note: f.get('note') } });
      toast(r.note || 'Booking cancelled');
      load();
    }
  }));

  root.querySelector('#new-booking')?.addEventListener('click', guard(async () => {
    const open = upcoming.filter((s) => s.status === 'open' && s.seats_left > 0);
    if (!open.length) { toast('There are no open dates with seats left. Add a date under Experiences first.', 'error'); return; }
    const f = await dialog(`<h2>Phone or in-person booking</h2>
      <p class="small muted">For guests who book with you directly. It's confirmed right away without an online deposit, so a reason is required.</p>
      <label class="field"><span>Date</span><select name="slotId" required>${open.map((s) => `<option value="${s.id}" ${s.id === state.slotId ? 'selected' : ''}>${esc(dateTime(s.starts_at))} · ${s.seats_left} left</option>`).join('')}</select></label>
      <div class="form-grid">
        <label class="field"><span>First name</span><input name="firstName" required></label>
        <label class="field"><span>Last name</span><input name="lastName"></label>
        <label class="field"><span>Email</span><input name="email" type="email" required></label>
        <label class="field"><span>Phone</span><input name="phone" type="tel"></label>
        <label class="field"><span>Guests</span><input name="guestCount" type="number" min="1" max="50" value="2" required></label>
      </div>
      ${reasonFields()}
      <div class="dialog-actions"><button class="btn small" value="cancel" formnovalidate>Back</button><button class="btn small primary" value="ok">Add booking</button></div>`, needNote);
    if (!f) return;
    await api('/bookings', { method: 'POST', body: { ...Object.fromEntries(f), guestCount: Number(f.get('guestCount')) } });
    toast('Booking added');
    state.slotId = f.get('slotId');
    root.querySelector('#slot').value = state.slotId;
    load();
  }));

  load();
}

const needNote = (f) => (f.get('reason') === 'Other' && !String(f.get('note') || '').trim() ? 'Please add a note explaining the override.' : null);

function reasonFields() {
  return `<label class="field"><span>Reason</span><select name="reason" required>${REASONS.map((r) => `<option>${r}</option>`).join('')}</select></label>
    <label class="field"><span>Note (required for "Other")</span><textarea name="note" rows="2" placeholder="Context for the owners"></textarea></label>
    <div data-error></div>`;
}
